import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { TwitterAnalyticsPostsSortOrder, TwitterAnalyticsPostItemPayload } from '@anytag/graphql';

export const useAnalyticsTwitterPostsListTable = () => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<TwitterAnalyticsPostItemPayload>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Posts'),
      cell: ({ row }) => (
        <TableCell.Post
          title={row.original.title || ''}
          thumbnail={row.original.thumbnail || ''}
          onClick={() => setPostDetailsParam(row.original.id)}
        />
      )
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.LIKES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.like)} />
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.VIEWS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.view)} />
    },
    {
      accessorKey: 'replies',
      header: t<string>('HeaderColumn.Reply'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.REPLIES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.reply)} />
    },
    {
      accessorKey: 'retweets',
      header: t<string>('HeaderColumn.Retweet'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.RETWEETS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.retweet)} />
    },
    {
      accessorKey: 'impressions',
      header: t<string>('HeaderColumn.Imps'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.IMPRESSIONS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.impression)} />
    },
    {
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.ENGAGEMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.engagement)} />
    },
    {
      accessorKey: 'engagementRate',
      header: t<string>('HeaderColumn.Engagement Rate'),
      meta: { align: 'right', sortField: TwitterAnalyticsPostsSortOrder.ENGAGEMENT_RATE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.engagementRate, false)} unit="%" />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: TwitterAnalyticsPostsSortOrder.POST_DATE },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postDate} />
    }
  ];

  return { columns };
};
